import React, { useState, useEffect } from 'react';
import { AlertTriangle, CheckCircle, XCircle, Activity, Users, Server, Database, Zap } from 'lucide-react';
import { QueueMonitoringFixed } from '../components/queue/QueueMonitoringFixed';
import { IngestionFlowWidget } from '../components/monitoring/IngestionFlowWidget';

interface SystemHealth {
  status: 'healthy' | 'degraded' | 'critical';
  timestamp: string;
  components: {
    database: ComponentHealth;
    cache: ComponentHealth;
    queue: ComponentHealth;
    workers: {
      l1: ComponentHealth;
      l2: ComponentHealth;
      l3: ComponentHealth;
    };
    ai: ComponentHealth;
  };
  performance: {
    throughput: {
      postsPerSecond: number;
      requestsPerSecond: number;
      cacheHitRate: number;
    };
    latency: {
      p50: number;
      p95: number;
      p99: number;
    };
    resources: {
      cpuUsage: number;
      memoryUsage: number;
      queueDepth: number;
    };
    errors: {
      rate: number;
      count: number;
      recentErrors: string[];
    };
  };
  alerts: Alert[];
  ingestion?: {
    status: 'healthy' | 'degraded' | 'unknown';
    itemsPerMinute: number;
    sources: Array<{
      name: string;
      itemsLastHour: number;
      lastIngested: string;
    }>;
    lastCheck: string;
  };
}

interface ComponentHealth {
  status: 'healthy' | 'degraded' | 'critical';
  latency?: number;
  errorRate?: number;
  availability?: number;
  lastCheck: string;
  details?: Record<string, any>;
}

interface Alert {
  id: string;
  severity: 'warning' | 'critical';
  component: string;
  message: string;
  timestamp: string;
  resolved?: boolean;
}

interface WorkerStatus {
  workers: Array<{
    name: string;
    status: 'healthy' | 'degraded' | 'critical';
    avgResponseTime: number;
    totalRequests: number;
    errorRate: number;
    successRate: number;
    circuitBreakerState: string;
    lastUpdated: string;
  }>;
  realtimeHealth: {
    L1: { status: string; circuitBreaker?: string };
    L2: { status: string; circuitBreaker?: string };
    L3: { status: string; circuitBreaker?: string };
  };
  summary: {
    totalWorkers: number;
    healthyWorkers: number;
    degradedWorkers: number;
    criticalWorkers: number;
  };
}

export const SystemMonitoring: React.FC = () => {
  const [systemHealth, setSystemHealth] = useState<SystemHealth | null>(null);
  const [workerStatus, setWorkerStatus] = useState<WorkerStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdated, setLastUpdated] = useState<Date>(new Date());

  // Mock API key for demonstration - in production this would be properly secured
  const API_KEY = 'demo-key-for-monitoring';

  const fetchMonitoringData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Fetch real monitoring data from API endpoints
      const [healthResponse, workersResponse] = await Promise.all([
        fetch('/api/v1/monitoring/health', {
          headers: {
            'Authorization': `Bearer ${API_KEY}`,
            'Content-Type': 'application/json'
          }
        }),
        fetch('/api/v1/monitoring/workers', {
          headers: {
            'Authorization': `Bearer ${API_KEY}`,
            'Content-Type': 'application/json'
          }
        })
      ]);

      if (!healthResponse.ok) {
        throw new Error(`Health endpoint failed: ${healthResponse.status} ${healthResponse.statusText}`);
      }

      if (!workersResponse.ok) {
        throw new Error(`Workers endpoint failed: ${workersResponse.status} ${workersResponse.statusText}`);
      }

      const healthData = await healthResponse.json();
      const workersData = await workersResponse.json();

      if (!healthData.success) {
        throw new Error(healthData.error || 'Failed to fetch health data');
      }

      if (!workersData.success) {
        throw new Error(workersData.error || 'Failed to fetch workers data');
      }

      setSystemHealth(healthData.data);
      setWorkerStatus(workersData.data);
      setLastUpdated(new Date());

    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch monitoring data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMonitoringData();

    // Refresh every 30 seconds
    const interval = setInterval(fetchMonitoringData, 30000);
    return () => clearInterval(interval);
  }, []);

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'healthy':
        return <CheckCircle className="w-5 h-5 text-green-500" />;
      case 'degraded':
        return <AlertTriangle className="w-5 h-5 text-yellow-500" />;
      case 'critical':
        return <XCircle className="w-5 h-5 text-red-500" />;
      default:
        return <Activity className="w-5 h-5 text-gray-500" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'healthy':
        return 'text-green-600 bg-green-50 border-green-200';
      case 'degraded':
        return 'text-yellow-600 bg-yellow-50 border-yellow-200';
      case 'critical':
        return 'text-red-600 bg-red-50 border-red-200';
      default:
        return 'text-gray-600 bg-gray-50 border-gray-200';
    }
  };

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto p-6">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          <span className="ml-2">Loading system health...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-7xl mx-auto p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex">
            <XCircle className="w-5 h-5 text-red-400" />
            <div className="ml-3">
              <h3 className="text-red-800 font-medium">Error Loading Monitoring Data</h3>
              <p className="text-red-700 mt-1">{error}</p>
              <button
                onClick={fetchMonitoringData}
                className="mt-2 bg-red-100 hover:bg-red-200 text-red-800 px-3 py-1 rounded text-sm"
              >
                Retry
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto p-6 space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">System Monitoring</h1>
          <p className="text-gray-600 mt-1">
            Real-time health and performance monitoring
          </p>
        </div>
        <div className="text-right">
          <div className="text-sm text-gray-500">Last updated</div>
          <div className="text-sm font-medium">{lastUpdated.toLocaleTimeString()}</div>
          <button
            onClick={fetchMonitoringData}
            className="mt-1 bg-blue-50 hover:bg-blue-100 text-blue-600 px-3 py-1 rounded text-sm"
          >
            Refresh
          </button>
        </div>
      </div>

      {/* System Overview */}
      {systemHealth && (
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          {/* Overall Status */}
          <div className={`p-6 rounded-lg border-2 ${getStatusColor(systemHealth.status)}`}>
            <div className="flex items-center">
              {getStatusIcon(systemHealth.status)}
              <div className="ml-3">
                <div className="text-sm font-medium uppercase tracking-wide">System Status</div>
                <div className="text-lg font-bold capitalize">{systemHealth.status}</div>
              </div>
            </div>
          </div>

          {/* Throughput */}
          <div className="bg-white p-6 rounded-lg border border-gray-200">
            <div className="flex items-center">
              <Activity className="w-5 h-5 text-blue-500" />
              <div className="ml-3">
                <div className="text-sm font-medium text-gray-500">Throughput</div>
                <div className="text-lg font-bold">
                  {(systemHealth.performance?.throughput?.postsPerSecond || 0) > 0 && (systemHealth.performance?.throughput?.postsPerSecond || 0) < 0.1 
                    ? '< 0.1' 
                    : (systemHealth.performance?.throughput?.postsPerSecond || 0).toFixed(1)} posts/sec
                </div>
                <div className="text-xs text-gray-400">
                  {(systemHealth.performance?.throughput?.requestsPerSecond || 0) > 0 && (systemHealth.performance?.throughput?.requestsPerSecond || 0) < 0.1
                    ? '< 0.1'
                    : (systemHealth.performance?.throughput?.requestsPerSecond || 0).toFixed(1)} req/sec
                </div>
              </div>
            </div>
          </div>

          {/* Cache Hit Rate */}
          <div className="bg-white p-6 rounded-lg border border-gray-200">
            <div className="flex items-center">
              <Zap className="w-5 h-5 text-green-500" />
              <div className="ml-3">
                <div className="text-sm font-medium text-gray-500">Cache Hit Rate</div>
                <div className="text-lg font-bold">{(systemHealth.performance?.throughput?.cacheHitRate || 0).toFixed(1)}%</div>
                <div className="text-xs text-gray-400">Target: 20%</div>
              </div>
            </div>
          </div>

          {/* Queue Depth */}
          <div className="bg-white p-6 rounded-lg border border-gray-200">
            <div className="flex items-center">
              <Users className="w-5 h-5 text-orange-500" />
              <div className="ml-3">
                <div className="text-sm font-medium text-gray-500">Queue Depth</div>
                <div className="text-lg font-bold">{systemHealth.performance?.resources?.queueDepth || 0}</div>
                <div className="text-xs text-gray-400">Pending messages</div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Component Health */}
      {systemHealth && (
        <div className="bg-white rounded-lg border border-gray-200">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">Component Health</h2>
          </div>
          <div className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {/* Database */}
              <div className="flex items-center p-4 bg-gray-50 rounded-lg">
                <Database className="w-8 h-8 text-blue-500" />
                <div className="ml-3">
                  <div className="flex items-center">
                    {getStatusIcon(systemHealth.components.database.status)}
                    <span className="ml-1 font-medium">Database</span>
                  </div>
                  <div className="text-sm text-gray-500">
                    {systemHealth.components.database.latency}ms latency
                  </div>
                </div>
              </div>

              {/* Cache */}
              <div className="flex items-center p-4 bg-gray-50 rounded-lg">
                <Zap className="w-8 h-8 text-green-500" />
                <div className="ml-3">
                  <div className="flex items-center">
                    {getStatusIcon(systemHealth.components.cache.status)}
                    <span className="ml-1 font-medium">Cache</span>
                  </div>
                  <div className="text-sm text-gray-500">
                    {(systemHealth.components.cache.details?.hitRate || 0).toFixed(1)}% hit rate
                  </div>
                </div>
              </div>

              {/* Queue */}
              <div className="flex items-center p-4 bg-gray-50 rounded-lg">
                <Users className="w-8 h-8 text-orange-500" />
                <div className="ml-3">
                  <div className="flex items-center">
                    {getStatusIcon(systemHealth.components.queue.status)}
                    <span className="ml-1 font-medium">Queue</span>
                  </div>
                  <div className="text-sm text-gray-500">
                    {(systemHealth.components.queue.details?.processingRate || 0).toFixed(1)}% processed
                  </div>
                </div>
              </div>

              {/* AI */}
              <div className="flex items-center p-4 bg-gray-50 rounded-lg">
                <Activity className="w-8 h-8 text-purple-500" />
                <div className="ml-3">
                  <div className="flex items-center">
                    {getStatusIcon(systemHealth.components.ai.status)}
                    <span className="ml-1 font-medium">AI</span>
                  </div>
                  <div className="text-sm text-gray-500">
                    {systemHealth.components.ai.latency}ms latency
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Worker Status */}
      {workerStatus && (
        <div className="bg-white rounded-lg border border-gray-200">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">Worker Performance</h2>
          </div>
          <div className="p-6">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {workerStatus.workers.map((worker) => (
                <div key={worker.name} className="border border-gray-200 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center">
                      <Server className="w-5 h-5 text-blue-500" />
                      <span className="ml-2 font-medium">{worker.name} Worker</span>
                    </div>
                    {getStatusIcon(worker.status)}
                  </div>

                  <div className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-500">Response Time</span>
                      <span className="font-medium">{worker.avgResponseTime}ms</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-500">Success Rate</span>
                      <span className="font-medium">{(worker.successRate || 0).toFixed(1)}%</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-500">Total Requests</span>
                      <span className="font-medium">{(worker.totalRequests || 0).toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-500">Circuit Breaker</span>
                      <span className="font-medium text-green-600">{worker.circuitBreakerState}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Performance Metrics */}
      {systemHealth && (
        <div className="bg-white rounded-lg border border-gray-200">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">Performance Metrics</h2>
          </div>
          <div className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {/* Latency */}
              <div>
                <h3 className="text-sm font-medium text-gray-700 mb-3">Response Time (ms)</h3>
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span className="text-gray-500">P50</span>
                    <span className="font-medium">{systemHealth.performance?.latency?.p50 || 0}ms</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">P95</span>
                    <span className="font-medium">{systemHealth.performance?.latency?.p95 || 0}ms</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">P99</span>
                    <span className="font-medium">{systemHealth.performance?.latency?.p99 || 0}ms</span>
                  </div>
                </div>
              </div>

              {/* Throughput */}
              <div>
                <h3 className="text-sm font-medium text-gray-700 mb-3">Throughput</h3>
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span className="text-gray-500">Posts/sec</span>
                    <span className="font-medium">{(systemHealth.performance?.throughput?.postsPerSecond || 0).toFixed(1)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">Requests/sec</span>
                    <span className="font-medium">{(systemHealth.performance?.throughput?.requestsPerSecond || 0).toFixed(1)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">Cache Hit Rate</span>
                    <span className="font-medium">{(systemHealth.performance?.throughput?.cacheHitRate || 0).toFixed(1)}%</span>
                  </div>
                </div>
              </div>

              {/* Errors */}
              <div>
                <h3 className="text-sm font-medium text-gray-700 mb-3">Error Metrics</h3>
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span className="text-gray-500">Error Rate</span>
                    <span className="font-medium">{(systemHealth.performance?.errors?.rate || 0).toFixed(2)}%</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">Error Count</span>
                    <span className="font-medium">{systemHealth.performance?.errors?.count || 0}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">Queue Depth</span>
                    <span className="font-medium">{systemHealth.performance?.resources?.queueDepth || 0}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Alerts */}
      {systemHealth && systemHealth.alerts.length > 0 && (
        <div className="bg-white rounded-lg border border-gray-200">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">Active Alerts</h2>
          </div>
          <div className="p-6">
            <div className="space-y-3">
              {systemHealth.alerts.map((alert) => (
                <div key={alert.id} className={`p-4 rounded-lg border ${alert.severity === 'critical' ? 'bg-red-50 border-red-200' : 'bg-yellow-50 border-yellow-200'
                  }`}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      {alert.severity === 'critical' ?
                        <XCircle className="w-5 h-5 text-red-500" /> :
                        <AlertTriangle className="w-5 h-5 text-yellow-500" />
                      }
                      <div className="ml-3">
                        <div className="font-medium">{alert.component}</div>
                        <div className="text-sm text-gray-600">{alert.message}</div>
                      </div>
                    </div>
                    <div className="text-sm text-gray-500">
                      {new Date(alert.timestamp).toLocaleTimeString()}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}



      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Ingestion Flow */}
        <IngestionFlowWidget data={systemHealth?.ingestion} />

        {/* Queue Monitoring Section */}
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-semibold">Queue Monitoring</h3>
            <a
              href="/queue-test"
              className="text-sm bg-purple-100 hover:bg-purple-200 text-purple-800 px-3 py-1 rounded"
            >
              🔧 Debug & Test
            </a>
          </div>
          <QueueMonitoringFixed apiKey={API_KEY} />
        </div>
      </div>

      {/* Footer */}
      <div className="text-center text-sm text-gray-500">
        <p>System monitoring dashboard - Updates every 30 seconds</p>
        <p className="mt-1">
          For detailed metrics and alerts, check the{' '}
          <a href="/api/v1/monitoring/health" className="text-blue-600 hover:text-blue-800">
            monitoring API
          </a>
          {' | '}
          <a href="/queue-test" className="text-purple-600 hover:text-purple-800">
            Queue Debug Tools
          </a>
        </p>
      </div>
    </div>
  );
};

export default SystemMonitoring;